import { Eye, Trash2 } from "lucide-react";
import {
  formatTaskDate,
  formatTaskMinutes,
  getTaskPriorityLabel,
  getTaskStateLabel,
  isTaskOverdue,
  type Task,
} from "../types/tasks";

type Props = {
  tasks: Task[];
  currentUserId: number;
  onOpenTask: (task: Task) => void;
  onDeleteTask?: (task: Task) => void;
};

export function TaskTableBoard({
  tasks,
  currentUserId,
  onOpenTask,
  onDeleteTask,
}: Props) {
  if (tasks.length === 0) {
    return (
      <section className="rounded-[34px] border border-dashed border-[#dfe8e4] bg-white/85 p-10 text-center shadow-[0_24px_70px_rgba(15,23,42,.08)]">
        <div className="text-lg font-black text-slate-950">Sin tareas visibles</div>
        <p className="mx-auto mt-2 max-w-md text-sm font-semibold leading-6 text-slate-500">
          Ajusta los filtros o crea una nueva tarea para empezar a trabajar sobre este módulo.
        </p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-[34px] border border-[#dfe8e4] bg-white shadow-[0_24px_70px_rgba(15,23,42,.08)]">
      <div className="overflow-x-auto">
        <table className="tasks-table min-w-full">
          <thead>
            <tr>
              <th>Tarea</th>
              <th>Proyecto</th>
              <th>Estado</th>
              <th>Prioridad</th>
              <th>Responsable</th>
              <th>Fecha inicio</th>
              <th>Fecha fin</th>
              <th>Subtareas</th>
              <th>Tiempo</th>
              <th>Comentarios</th>
              <th>Adjuntos</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {tasks.map((task) => (
              <tr key={task.id}>
                <td>
                  <div className="min-w-[240px]">
                    <div className="font-black text-slate-950">{task.titulo}</div>
                    <div className="mt-1 text-xs font-semibold text-slate-500">
                      {task.descripcion?.trim() || "Sin descripción"}
                    </div>
                  </div>
                </td>
                <td>{task.proyecto_nombre ?? "Sin proyecto"}</td>
                <td>
                  <span className="inline-flex rounded-full border border-[#00d09c]/18 bg-[#00d09c]/8 px-3 py-1 text-xs font-black text-[#0b4a2f]">
                    {getTaskStateLabel(task.estado)}
                  </span>
                </td>
                <td>{getTaskPriorityLabel(task.prioridad)}</td>
                <td>{task.asignado_nombre ?? "Sin asignar"}</td>
                <td>{formatTaskDate(task.fecha_inicio)}</td>
                <td>
                  <span className={isTaskOverdue(task) ? "text-amber-700" : ""}>
                    {formatTaskDate(task.fecha_fin)}
                  </span>
                </td>
                <td>
                  {task.subtareas_completadas}/{task.subtareas_total}
                </td>
                <td>{formatTaskMinutes(task.minutos_total)}</td>
                <td>{task.comentarios_total}</td>
                <td>{task.adjuntos_total}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onOpenTask(task)}
                      className="inline-flex items-center gap-1 rounded-full border border-[#dfe8e4] bg-white px-3 py-1.5 text-xs font-black text-slate-600"
                    >
                      <Eye size={13} />
                      Abrir
                    </button>

                    {onDeleteTask && task.creado_por === currentUserId && (
                      <button
                        type="button"
                        onClick={() => onDeleteTask(task)}
                        className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-black text-rose-700"
                      >
                        <Trash2 size={13} />
                        Eliminar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}